import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, castArray, uniqBy, isNil, get, differenceWith, omit } from 'lodash';
import { Form, FormItemProps, Select, Spin, SelectProps as AntSelectProps, Empty } from 'antd';
import { SelectValue } from 'antd/lib/select';
import { getSearchRule, Rule, transformRules } from '../utils/rules';
import { logger } from '../utils';
import InfoTooltip from '../components/InfoTooltip';
import { UseAPI, Variables } from '../api';
const LOAD_MORE_OPTION_VALUE = '__FORM_SEARCH_LOAD_MORE__';

export type SearchOption = {
  label: string;
  value: string | number;
  extra: PlainObject<any>;
};

export interface FormSearchProps {
  name: FormItemProps['name'];
  label: string;
  api: UseAPI;
  tooltip?: PropPresets.Tooltip;
  type?: 'string' | 'number';
  placeholder?: string;
  disabled?: boolean;
  rules?: Rule[];
  mode?: 'default' | 'multiple';
  searchKey?: string;
  dataKey?: string;
  labelKey?: string | string[];
  valueKey?: string;
  onSelectChange?: (selectedOptions: SearchOption[]) => void;
  searchProps?: AntSelectProps<SelectValue>;
  formItemProps?: FormItemProps;
  initialValue?: number | string | number[] | string[];
  apiParams?: Variables;
  onFinish?: (values: SearchOption[]) => void;
  validateOptions?: boolean;
}

function FormSearch(props: FormSearchProps) {
  const {
    name,
    label,
    tooltip,
    api: useApi,
    placeholder = label,
    type = 'string',
    disabled = false,
    mode: propMode = 'default',
    searchKey = 'ids',
    dataKey = 'data.items',
    labelKey = 'label',
    valueKey = 'value',
    rules: propRules,
    initialValue,
    searchProps,
    formItemProps,
    onSelectChange,
    apiParams,
    onFinish,
    validateOptions = true,
  } = props;
  const [selectedOptions, setSelectedOptions] = useState<SearchOption[]>([]);
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [api, { loading }] = useApi();
  const lastFetchId = useRef(0);
  const pendingPromise = useRef<Promise<SearchOption[]>>(Promise.resolve([]));
  const currentPage = useRef(1);
  const currentPhrase = useRef('');
  const loadingMore = useRef(false);
  const loadMoreObserver = useRef<IntersectionObserver | null>(null);
  const PAGE_SIZE = 25;

  const allOptions = useMemo(
    () => uniqBy([...selectedOptions, ...options].filter(Boolean), (option) => option.value),
    [selectedOptions, options],
  );

  const rules = useMemo(
    () =>
      transformRules(
        {
          type: propMode === 'multiple' ? 'array' : type,
        },
        [
          ...(propRules || []),
          ...(validateOptions
            ? [
              getSearchRule(async () => {
                const opts = await pendingPromise.current;
                return [...allOptions, ...opts];
              }),
            ]
            : []),
        ],
      ),
    [allOptions, propMode, propRules, type, validateOptions],
  );

  const fetchData = useCallback(
    async (val: string | PlainObject<any> = '', page = 1): Promise<SearchOption[]> => {
      let params = { ...apiParams };
      if (typeof val === 'string') params.phrase = val;
      else params = { ...val, ...params };
      return new Promise((resolve, reject) => {
        api({ params: { ...params, limit: PAGE_SIZE, page } })
          .then((apiData) => {
            const items: SearchOption[] =
              get(apiData, dataKey).map(
                (data: { label: string | PlainObject<string>; value: string | number }) => {
                  const searchLabel: string = get(data, labelKey);
                  return {
                    label: searchLabel,
                    value: get(data, valueKey),
                    extra: omit(data, [castArray(labelKey)[0], valueKey]),
                  };
                },
              ) || [];
            resolve(items);
            onFinish?.(items);
          })
          .catch(reject);
      });
    },
    [api, apiParams, dataKey, labelKey, onFinish, valueKey],
  );

  const addOptions = useMemo(
    () =>
      debounce(async (phrase = '') => {
        lastFetchId.current += 1;
        const fetchId = lastFetchId.current;
        currentPage.current = 1;
        currentPhrase.current = phrase;

        try {
          const result = await fetchData(phrase, 1);
          // Don't set for older calls
          if (fetchId !== lastFetchId.current) return;
          setOptions(result);
          setHasMore(result.length >= PAGE_SIZE);
        }
        catch (err) {
          logger.error({ err }, 'Form Search Error');
        }
      }, 500),
    [fetchData],
  );

  const loadMore = useCallback(async () => {
    loadingMore.current = true;
    const nextPage = currentPage.current + 1;
    currentPage.current = nextPage;
    try {
      const result = await fetchData(currentPhrase.current, nextPage);
      setOptions((prev) => uniqBy([...prev, ...result], (option) => option.value));
      setHasMore(result.length >= PAGE_SIZE);
    }
    catch (err) {
      logger.error({ err }, 'FormSearch loadMore Error');
    }
    finally {
      loadingMore.current = false;
    }
  }, [fetchData]);

  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      loadMoreObserver.current?.disconnect();
      if (!node || !hasMore) return;
      loadMoreObserver.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) loadMore();
        },
        {
          root: node.closest('.rc-virtual-list-holder'),
          rootMargin: '0px 0px 24px 0px',
        },
      );
      loadMoreObserver.current.observe(node);
    },
    [hasMore, loadMore],
  );

  const selectOptions = useMemo<AntSelectProps<SelectValue>['options']>(() => {
    if (!hasMore) return allOptions;
    return [
      ...allOptions,
      {
        disabled: true,
        label: <div ref={loadMoreRef} style={{ height: 1, width: '100%' }} />,
        value: LOAD_MORE_OPTION_VALUE,
      },
    ];
  }, [allOptions, hasMore, loadMoreRef]);

  useEffect(() => {
    setOptions([]);
  }, []);

  useEffect(() => () => {
    loadMoreObserver.current?.disconnect();
  },[]);

  // For Edit Case
  useEffect(() => {
    let isMounted = true;
    const initVals = castArray(initialValue || []);
    if (isNil(initVals) || !initVals.length)
      return () => {
        isMounted = false;
      };

    if (valueKey !== 'value') {
      const selected = initVals.map((val) => ({
        label: String(val),
        value: val,
        extra: {},
      }));
      pendingPromise.current = Promise.resolve(selected);
      setSelectedOptions(selected);
      return () => {
        isMounted = false;
      };
    }

    pendingPromise.current = fetchData({ [searchKey]: initVals, limit: initVals.length })
      .then((result) => {
        const selected = result.filter((item) => initVals.includes(item.value));
        if (isMounted) setSelectedOptions(selected);
        return selected;
      })
      .catch((err) => {
        logger.error({ err }, 'Error while FormSearch init');
        return [];
      });

    return () => {
      isMounted = false;
    };
  }, [initialValue, searchKey, fetchData, valueKey]);

  const onSelect: AntSelectProps<SelectValue>['onSelect'] = useCallback(
    (val: any) => {
      const optionToAdd = options.find((option) => option.value === val);
      if (optionToAdd) {
        setSelectedOptions((prevVals) => {
          const newOptions = propMode === 'multiple' ? [...prevVals, optionToAdd] : [optionToAdd];
          onSelectChange?.(newOptions);
          return newOptions;
        });
      }
    },
    [options, onSelectChange, propMode],
  );

  const onDeselect: AntSelectProps<SelectValue>['onDeselect'] = useCallback(
    (val: any) => {
      setSelectedOptions((prevVals) => {
        const newOptions = prevVals.filter((option) => option.value !== val);
        onSelectChange?.(newOptions);
        return newOptions;
      });
    },
    [onSelectChange],
  );

  // initialize options on focus
  const onFocus: AntSelectProps<SelectValue>['onFocus'] = useCallback(() => {
    if (!options?.length) addOptions();
  }, [options, addOptions]);

  const onClear: AntSelectProps<SelectValue>['onClear'] = useCallback(() => {
    if (selectedOptions.length) {
      onSelectChange?.([]);
      setSelectedOptions([]);
    }
  }, [selectedOptions, onSelectChange]);

  const onChange: AntSelectProps<SelectValue>['onChange'] = useCallback(
    (values: any) => {
      const ids = values.filter((val: string) => val.match(/^[a-fA-F0-9]{24}$/));
      if (differenceWith(ids, allOptions, (id, option) => id === option.value).length) {
        pendingPromise.current = fetchData({ ids })
          .then((res) => {
            setOptions(res);
            return res;
          })
          .catch((err) => {
            logger.error({ err }, 'Error in FormSearch onChange');
            return [];
          });
      }
    },
    [allOptions, fetchData],
  );

  const getPopupContainer: AntSelectProps<SelectValue>['getPopupContainer'] = useCallback(
    (node: any) => node.closest('.ant-card') || node.closest('.ant-form-item'),
    [],
  );

  return (
    <Form.Item
      hasFeedback
      name={name}
      label={label}
      rules={rules}
      tooltip={InfoTooltip.Config(tooltip)}
      validateFirst
      initialValue={initialValue}
      {...formItemProps}
    >
      <Select
        showSearch
        // Disable filter as results are already filtered from backend
        filterOption={false}
        allowClear
        mode={propMode !== 'default' ? 'tags' : undefined}
        tokenSeparators={[',']}
        placeholder={placeholder}
        onFocus={onFocus}
        onSearch={addOptions}
        onSelect={onSelect}
        onDeselect={onDeselect}
        onClear={onClear}
        onChange={propMode !== 'default' ? onChange : undefined}
        disabled={disabled}
        optionFilterProp="label"
        getPopupContainer={getPopupContainer}
        {...searchProps}
        notFoundContent={
          loading ? (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <Spin />
            </div>
          ) : (
            <Empty
              description={`No ${label} Found`}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ margin: '8px 0' }}
            />
          )
        }
        options={selectOptions}
      />
    </Form.Item>
  );
}

export default FormSearch;
